import { useEffect } from 'react';

/**
 * Warns on page unload when a signer has completed every field of a workflow
 * but has not clicked Finish Signing. Most browsers ignore custom beforeunload
 * text and show a generic "leave this site?" dialog. The point is that
 * confirmation, not the wording; the string below is set for the few browsers
 * that still display it, and matches the walkthrough app-bar copy.
 *
 * This is deliberately not react-use's useBeforeUnload. That hook removes its
 * listener in effect cleanup, and editor panes live inside React.Activity,
 * which runs cleanup on every Aline tab hide. A signer who filled every field
 * then switched tabs would lose the warning. The listener is module-level and
 * this hook's effect has no cleanup, so hide/show only re-syncs. The same
 * pattern as `uploadUnloadGuard`.
 *
 * Guard state is keyed by workflow id so multiple editor panes (Aline tabs)
 * do not overwrite each other. The listener stays attached while any workflow
 * still needs its Finish click.
 */
export const FINISH_SIGNING_UNLOAD_MESSAGE = 'Click "Finish Signing" to complete your signature.';

const workflowsNeedingFinish = new Set<string>();

export function useFinishSigningUnloadGuard(
  workflowId: string | null | undefined,
  shouldWarn: boolean
): void {
  useEffect(() => {
    if (!workflowId) return;
    setFinishSigningUnloadGuardForWorkflow(workflowId, shouldWarn);
  }, [workflowId, shouldWarn]);
}

export function setFinishSigningUnloadGuardForWorkflow(
  workflowId: string,
  needsFinish: boolean
): void {
  const wasEnabled = workflowsNeedingFinish.size > 0;
  if (needsFinish) workflowsNeedingFinish.add(workflowId);
  else workflowsNeedingFinish.delete(workflowId);
  const isEnabled = workflowsNeedingFinish.size > 0;
  if (wasEnabled === isEnabled) return;
  if (isEnabled) window.addEventListener('beforeunload', warnBeforeUnload);
  else window.removeEventListener('beforeunload', warnBeforeUnload);
}

export function clearFinishSigningUnloadGuard(): void {
  const wasEnabled = workflowsNeedingFinish.size > 0;
  workflowsNeedingFinish.clear();
  if (wasEnabled) window.removeEventListener('beforeunload', warnBeforeUnload);
}

export function getFinishSigningUnloadGuardEnabled(): boolean {
  return workflowsNeedingFinish.size > 0;
}

function warnBeforeUnload(event: BeforeUnloadEvent): void {
  event.preventDefault();
  // Chrome only shows the confirmation dialog when returnValue is set.
  event.returnValue = FINISH_SIGNING_UNLOAD_MESSAGE;
}
